import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Switch — accessible pill toggle. Use for boolean on/off settings.
 *
 * `checked` + `onCheckedChange(checked: boolean)` is the controlled API
 * (matches shadcn/Headless UI convention). The rendered element is a
 * `<button role="switch" aria-checked={checked}>` so it gets keyboard
 * activation (Enter / Space) for free.
 *
 * Pass `aria-label` or `aria-labelledby` so screen readers can announce
 * what the toggle controls — the switch has no visible text of its own.
 */
export const switchVariants = cva(
  'relative inline-flex items-center rounded-full transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent-mint-500/40 ' +
    'disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'h-5 w-9',
        md: 'h-6 w-11',
        lg: 'h-7 w-12',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const THUMB_SIZE = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

const THUMB_TRANSLATE = {
  sm: { off: 'translate-x-0.5', on: 'translate-x-[18px]' },
  md: { off: 'translate-x-1',   on: 'translate-x-6' },
  lg: { off: 'translate-x-1',   on: 'translate-x-6' },
} as const;

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'type' | 'aria-checked'>,
    VariantProps<typeof switchVariants> {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, size, checked, onCheckedChange, disabled, onClick, ...props }, ref) => {
    const resolvedSize = size ?? 'md';
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          onClick?.(e);
          if (!e.defaultPrevented) {
            onCheckedChange?.(!checked);
          }
        }}
        className={cn(
          switchVariants({ size: resolvedSize }),
          checked ? 'bg-accent-mint-500' : 'bg-ink-300',
          className
        )}
        {...props}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-block rounded-full bg-surface shadow-sm transition-transform',
            THUMB_SIZE[resolvedSize],
            checked ? THUMB_TRANSLATE[resolvedSize].on : THUMB_TRANSLATE[resolvedSize].off
          )}
        />
      </button>
    );
  }
);
Switch.displayName = 'Switch';
